import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {
    Config,
    Measure,
    MeasureType,
    MeteomaticsConfig,
    SourceConfig,
    SourceType
} from 'smag-commons/cfg/connector/meteo';
import {AsyncValidatorFn, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject, merge, Observable, of, Subject, Subscription} from 'rxjs';
import * as _ from 'lodash';
import {isBlank, keySorter} from '../../../commons/commons';
import {map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {
    validateFloat,
    validateGt,
    validateIfDirty,
    validateInteger,
    validateNotBlank,
    validateNotDuplicate
} from '../../../commons/validators';
import {
    DEFAULT_MAX_CHARS,
    EDITOR_BUILTIN_FUNCTIONS,
    EDITOR_BUILTIN_TYPES,
    HOST_MAX_CHARS,
    HOST_PATTERN,
    ID_MAX_CHARS,
    ID_PATTERN,
    METEO_DEFAULT_DURATION,
    METEO_DEFAULT_GRANULARITY,
    METEO_DEFAULT_MEASURES,
    METEO_DEFAULT_OFFSET,
    METEO_DEFAULT_POLLING_CRON,
    METEO_DEFAULT_POLLING_CRON_TIMEZONE,
    METEO_MEASURE_TYPES,
    METEO_SRC_TYPES,
    METEO_SUGGESTED_UOMS
} from '../connectors';
import {ConfigurationService} from '../../../shared/configuration.service';
import {
    PollingPolicy,
    PollingPolicyComponent,
    PollingPolicyData,
    setDevicePolicy
} from '../polling-policy/polling-policy.component';
import {MatSelectChange} from '@angular/material/select';
import {Features} from '../expression-editor/expression-editor.component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NService} from '../../../i18n/i18n.service';
import {SmagApiService} from '../../../api/smag-api.service';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';

interface MeasureConfig {
    measId: string;
    form: FormGroup;
    newlyCreated: boolean; // "newly" = "not saved yet"
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface VirtualDeviceConfig {
    vdevId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
    measures: { [measId: string]: MeasureConfig };
    newlyCreated: boolean; // "newly" = "not saved yet"
}

interface PagedVirtualDevices {
    data: VirtualDeviceConfig[];
    total: number;
}

const VARIABLE_ACCESSORS = ['v']; // "variable"

const VDEV_ADD_DIALOG_MAX_WIDTH = 500;
const VDEV_RENAME_DIALOG_MAX_WIDTH = 500;
const VDEV_DELETE_DIALOG_MAX_WIDTH = 500;
const VDEV_POLLING_POLICY_DIALOG_MAX_WIDTH = 500;
const VDEV_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH = 500;
const MEAS_ADD_DIALOG_MAX_WIDTH = 500;
const MEAS_RENAME_DIALOG_MAX_WIDTH = 500;
const MEAS_DELETE_DIALOG_MAX_WIDTH = 500;
const MEAS_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH = 500;
const PAGE_SIZE = 5;
const PAGE_SIZES = [5, 10, 15];

const PASSWORD_PLACEHOLDER = '**********';

@Component({
    selector: 'app-meteo-config',
    templateUrl: './meteo-config.component.html',
    styleUrls: ['./meteo-config.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Locations per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class MeteoConfigComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private sysTimeZone: string;
    private sysTimeResolution: number; // minutes-per-unit
    private reloadPages: Subject<void>;
    @ViewChild('addVirtualDevice') private addVirtualDeviceDialog: TemplateRef<any>;
    @ViewChild('renameVirtualDevice') private renameVirtualDeviceDialog: TemplateRef<any>;
    @ViewChild('deleteVirtualDevice') private deleteVirtualDeviceDialog: TemplateRef<any>;
    @ViewChild('advancedVirtualDeviceSettings') private advancedVirtualDeviceSettingsDialog: TemplateRef<any>;
    @ViewChild('addMeasure') private addMeasureDialog: TemplateRef<any>;
    @ViewChild('renameMeasure') private renameMeasureDialog: TemplateRef<any>;
    @ViewChild('deleteMeasure') private deleteMeasureDialog: TemplateRef<any>;
    @ViewChild('advancedMeasureSettings') private advancedMeasureSettingsDialog: TemplateRef<any>;
    @ViewChild('paginator') private paginator: MatPaginator;
    @ViewChild('deviceSearch') deviceSearch: ElementRef;

    pagedVDevs: BehaviorSubject<PagedVirtualDevices>;

    @Input('id') id: string;
    @Input('config') config: Config;
    @Output('configChange') configEmitter: EventEmitter<Config> = new EventEmitter<Config>();
    @Output('valid') validEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    cfg: Config;
    form: FormGroup;
    virtualDevices: VirtualDeviceConfig[];
    vDevToExpand: string;
    deviceSearchCtrl: FormControl;
    filteredDevices: Observable<VirtualDeviceConfig[]>;
    selectedDev: VirtualDeviceConfig;

    // exported in HTML
    METEO_SRC_TYPES = METEO_SRC_TYPES;
    SourceType = SourceType;
    METEO_MEASURE_TYPES = METEO_MEASURE_TYPES;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    HOST_MAX_CHARS = HOST_MAX_CHARS;
    ID_MAX_CHARS = ID_MAX_CHARS;
    DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly appCfg: AppConfig,
                private readonly apiService: SmagApiService,
                private readonly configurationService: ConfigurationService,
                private fb: FormBuilder,
                private readonly dialog: MatDialog,
                private readonly changeDetectorRef: ChangeDetectorRef) {
        this.sysTimeZone = null;
        this.sysTimeResolution = null;
        this.reloadPages = new Subject<void>();
        this.pagedVDevs = new BehaviorSubject<PagedVirtualDevices>(null);
        this.vDevToExpand = null;
        this.deviceSearchCtrl = this.fb.control('');
        this.selectedDev = null;
    }

    ngOnInit() {
        this.configurationService.timeConfig.pipe(
            takeUntil(this.destroyed)
        ).subscribe(timeCfg => {
            this.sysTimeZone = timeCfg.time_zone;
            this.sysTimeResolution = timeCfg.time_resolution;
        });
        // NOTE: config is not supposed to be changed externally outside of component's creation
        this.cfg = _.cloneDeep(this.config);
        this.form = this.fb.group({
            srcType: new FormControl(this.cfg.src_cfg.type, [Validators.required])
        });
        this.updateSourceControls(this.cfg.src_cfg); // done *before* subscribing to changes
        this.virtualDevices = Object.keys(this.cfg.devices).map(vdevId => this.mkVirtualDeviceConfig(vdevId, false));
        this.virtualDevices.sort(keySorter('vdevId'));
        this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            this.updateSourceConfig(value);
            this.updateConfig();
        });
        this.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        // do not send an initial config update, as it may trigger a (useless) REST call
        this.updateValidity();
        this.filteredDevices = this.deviceSearchCtrl.valueChanges.pipe(
            startWith(''),
            map(event => typeof event === 'string' ? event : (event as VirtualDeviceConfig).vdevId),
            map(vdevId => vdevId ? this.filterDevices(vdevId) : this.virtualDevices.slice()),
            takeUntil(this.destroyed)
        );
    }

    ngAfterViewInit() {
        merge(this.paginator.page,
            this.reloadPages.pipe(tap(() => this.paginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedVirtualDevices(this.paginator.pageSize, this.paginator.pageIndex).pipe(
                    startWith(null as PagedVirtualDevices) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedVDevs => {
            if (pagedVDevs) {
                this.pagedVDevs.next(pagedVDevs);
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.complete();
        this.destroyed.next();
    }

    trackByVdevId(index: number, vdevCfg: VirtualDeviceConfig): string {
        return vdevCfg.vdevId;
    }

    trackByMeasId(index: number, measCfg: MeasureConfig): string {
        return measCfg.measId;
    }

    getVdevMeasures(vdev: VirtualDeviceConfig): MeasureConfig[] {
        const measures = _.sortBy(_.values(vdev.measures), meas => meas.measId);
        return measures.length ? measures : null; // to aid *ngIf
    }

    hasMeasure(vdevCfg: VirtualDeviceConfig, measCfg: MeasureConfig, measType: MeasureType): boolean {
        const measures = this.cfg.devices[vdevCfg.vdevId].measures;
        return !!measures && !!_.values(measures).find(m => m.type === measType);
    }

    advancedSettingsEnabled(vdevCfg: VirtualDeviceConfig, measCfg: MeasureConfig): boolean {
        return advancedSettingsEnabled(this.cfg.devices[vdevCfg.vdevId].measures[measCfg.measId]);
    }

    onSourceTypeChange(event: MatSelectChange) {
        // clear source config
        this.cfg.src_cfg = {
            type: event.value as SourceType
        };
        // update controls
        this.updateSourceControls(this.cfg.src_cfg);
    }

    onClickVirtualDeviceAddMeasure(vdev: VirtualDeviceConfig) {
        this.dialog.open(this.addMeasureDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank, Validators.pattern(ID_PATTERN)],
                    [this.mkMeasureUniquenessValidator()])
            },
            maxWidth: `${MEAS_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addMeasure(vdev, id);
            }
        });
    }

    onClickAddVirtualDevice() {
        const virtualDevices = this.virtualDevices.map(vdev => vdev.vdevId);
        this.dialog.open(this.addVirtualDeviceDialog, {
            data: {
                idFormControl: new FormControl('',
                    [validateNotBlank, Validators.pattern(ID_PATTERN), validateNotDuplicate(virtualDevices)])
            },
            maxWidth: `${VDEV_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addVirtualDevice(id);
                this.resetDeviceSearch();
            }
        });
    }

    onClickRenameVirtualDevice(vdevCfg: VirtualDeviceConfig) {
        const virtualDevices = this.virtualDevices.map(vdev => vdev.vdevId).filter(id => id !== vdevCfg.vdevId);
        this.dialog.open(this.renameVirtualDeviceDialog, {
            data: {
                vdevId: vdevCfg.vdevId,
                relatedMeasures: _.values(vdevCfg.measures).map(m => m.measId),
                idFormControl: new FormControl(vdevCfg.vdevId, [validateNotBlank, Validators.pattern(ID_PATTERN),
                    validateNotDuplicate(virtualDevices)])
            },
            maxWidth: `${VDEV_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== vdevCfg.vdevId) {
                this.renameVirtualDevice(vdevCfg.vdevId, newName);
                this.resetDeviceSearch();
            }
        });
    }

    onClickDeleteVirtualDevice(vdevCfg: VirtualDeviceConfig) {
        this.dialog.open(this.deleteVirtualDeviceDialog, {
            data: {
                vdevId: vdevCfg.vdevId,
                relatedMeasures: _.values(vdevCfg.measures).map(m => m.measId)
            },
            maxWidth: `${VDEV_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteVirtualDevice(vdevCfg.vdevId);
                this.resetDeviceSearch();
            }
        });
    }

    onClickVirtualDevicePollingPolicy(vdevId: string) {
        this.dialog.open(PollingPolicyComponent, {
            data: {
                devId: vdevId,
                dev: this.cfg.devices[vdevId]
            } as PollingPolicyData,
            maxWidth: `${VDEV_POLLING_POLICY_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe((policy: PollingPolicy) => {
            if (policy) {
                this.setVirtualDevicePolicy(vdevId, policy);
            }
        });
    }

    onClickVirtualDeviceAdvancedSettings(vdevCfg: VirtualDeviceConfig) {
        const form = this.fb.group({
            offset: new FormControl(this.cfg.devices[vdevCfg.vdevId].offset, [Validators.required, validateInteger, Validators.min(0)]),
            granularity: new FormControl(this.cfg.devices[vdevCfg.vdevId].granularity, [Validators.required, validateInteger, validateGt(0)])
        });
        this.dialog.open(this.advancedVirtualDeviceSettingsDialog, {
            data: {
                vdevId: vdevCfg.vdevId,
                form
            },
            maxWidth: `${VDEV_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(keep => {
            if (keep) {
                this.setVirtualDeviceAdvancedSettings(vdevCfg,
                    form.controls.offset.value,
                    form.controls.granularity.value);
            }
        });
    }

    onClickRenameMeasure(vdevCfg: VirtualDeviceConfig, measId: string) {
        this.dialog.open(this.renameMeasureDialog, {
            data: {
                measId,
                idFormControl: new FormControl(measId, [validateNotBlank, Validators.pattern(ID_PATTERN)],
                    [this.mkMeasureUniquenessValidator(measId)])
            },
            maxWidth: `${MEAS_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== measId) {
                this.renameMeasure(vdevCfg, measId, newName);
            }
        });
    }

    onClickDeleteMeasure(vdevCfg: VirtualDeviceConfig, measId: string) {
        this.dialog.open(this.deleteMeasureDialog, {
            data: {
                measId
            },
            maxWidth: `${MEAS_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteMeasure(vdevCfg, measId);
            }
        });
    }

    onClickMeasureAdvancedSettings(vdevCfg: VirtualDeviceConfig, measCfg: MeasureConfig) {
        const form = this.fb.group({
            expression: new FormControl(this.cfg.devices[vdevCfg.vdevId].measures[measCfg.measId].expression, [])
        });
        this.dialog.open(this.advancedMeasureSettingsDialog, {
            data: {
                measId: measCfg.measId,
                form,
                features: this.mkEditorFeatures()
            },
            maxWidth: `${MEAS_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(keep => {
            if (keep) {
                this.setMeasureAdvancedSettings(vdevCfg, measCfg,
                    form.controls.expression.value);
            }
        });
    }

    onMeasureTypeChange(vdevCfg: VirtualDeviceConfig, measCfg: MeasureConfig, event: MatSelectChange) {
        // enable/disable uom according to measure type
        if (uomEnabled(this.cfg.devices[vdevCfg.vdevId].measures[measCfg.measId])) {
            measCfg.form.controls.uom.enable();
        } else {
            measCfg.form.controls.uom.disable();
        }
        // weather state has no uom (and expression) at all
        if (event.value as MeasureType === MeasureType.WEATHER_STATE) {
            measCfg.form.controls.uom.setValue(null);
            this.setMeasureAdvancedSettings(vdevCfg, measCfg, null);
            return;
        }
        // if uom is currently not set for this measure, set it to a (type-dependent) suggested one
        const uom = measCfg.form.controls.uom.value;
        if (!uom || !uom.trim().length) {
            const suggestedUoms = METEO_SUGGESTED_UOMS[this.cfg.src_cfg.type];
            if (suggestedUoms) {
                const suggestedUom = suggestedUoms[event.value as MeasureType];
                if (suggestedUom) {
                    measCfg.form.controls.uom.setValue(suggestedUom);
                }
            }
        }
    }

    onPasswordControlInteraction() {
        const pwdControl = this.form.controls['pass'];
        if (!pwdControl.dirty) {
            pwdControl.markAsDirty();
            pwdControl.setValue(null); // remove placeholder
        }
    }

    onDeviceSearchLoseFocus(event: FocusEvent) {
        // do NOT reset control if an actual device has just been selected
        if (!(event.relatedTarget as Element)?.getAttribute('data-connectorDevice') &&
            !this.virtualDevices.find(device => device.vdevId === this.deviceSearchCtrl.value.vdevId)) {
            // we have lost focus on (autocomplete) input, but not because we have selected an actual option:
            // simulate the dismissal of the constraint form
            this.resetDeviceSearch();
        }
    }

    onClickResetDeviceSearch() {
        this.resetDeviceSearch();
    }

    displaySearchDevice(vdevCfg: VirtualDeviceConfig): string {
        return vdevCfg.vdevId;
    }

    onSelectSearchDevice(event: MatAutocompleteSelectedEvent) {
        this.selectedDev = event.option.value as VirtualDeviceConfig;
        this.deviceSearch.nativeElement.blur();
    }

    private resetDeviceSearch() {
        this.selectedDev = null;
        this.deviceSearchCtrl.setValue('');
    }

    private filterDevices(vdevId: string): VirtualDeviceConfig[] {
        const filterValue = vdevId.toLowerCase().trim();
        return this.virtualDevices.filter(vdevCfg => vdevCfg.vdevId.toLowerCase().indexOf(filterValue) >= 0);
    }

    private get isValid(): boolean {
        return this.form.valid &&
            this.virtualDevices.every(vdev => vdev.form.valid &&
                Object.values(vdev.measures).every(m => m.form.valid));
    }

    private updateConfig() {
        this.configEmitter.emit(this.cfg);
    }

    private updateValidity() {
        this.validEmitter.emit(this.isValid);
    }

    private mkEditorFeatures(): Features {
        const cfg = this.appCfg.exprEditor;
        return {
            keywords: _.fromPairs([
                ...EDITOR_BUILTIN_TYPES.map(t => [t, {color: cfg.builtinTypesColor, bold: cfg.builtinsBold}]),
                ...EDITOR_BUILTIN_FUNCTIONS.map(f => [f, {color: cfg.builtinFunctionsColor, bold: cfg.builtinsBold}]),
                ...VARIABLE_ACCESSORS.map(f => [f, {
                    color: cfg.variableAccessorsColor,
                    bold: cfg.variableAccessorBold
                }])
            ])
        };
    }

    private updateSourceControls(srcCfg: SourceConfig) {
        // clear form controls and add source-specific controls
        Object.keys(this.form.controls)
            .filter(ctrl => ctrl !== 'srcType')
            .forEach(ctrl => this.form.removeControl(ctrl));
        switch (srcCfg.type) {
            case SourceType.METEOMATICS:
                this.form.addControl('apiUrl', new FormControl((srcCfg as MeteomaticsConfig).api_url, [validateNotBlank, Validators.pattern(HOST_PATTERN)]));
                this.form.addControl('user', new FormControl((srcCfg as MeteomaticsConfig).username, [validateNotBlank]));
                this.form.addControl('pass', new FormControl((srcCfg as MeteomaticsConfig).password || PASSWORD_PLACEHOLDER, [validateIfDirty(validateNotBlank)]));
                break;
        }
        this.form.updateValueAndValidity();
    }

    private addVirtualDevice(vdevId: string) {
        // config must be updated in order for mkVirtualDeviceConfig() to work
        this.cfg.devices[vdevId] = {
            cron: METEO_DEFAULT_POLLING_CRON,
            timezone: this.sysTimeZone || METEO_DEFAULT_POLLING_CRON_TIMEZONE,
            offset: METEO_DEFAULT_OFFSET,
            duration: METEO_DEFAULT_DURATION,
            granularity: this.sysTimeResolution ? this.sysTimeResolution * 60 : METEO_DEFAULT_GRANULARITY,
            latitude: null,
            longitude: null,
            measures: this.cfg.src_cfg?.type ?
                _.fromPairs((METEO_DEFAULT_MEASURES[this.cfg.src_cfg.type] || [])
                    .map(measure => [
                        `${vdevId.toLowerCase()}_${measure.suffix}`,
                        {
                            type: measure.type,
                            uom: measure.uom,
                            metadata: measure.metadata,
                            expression: measure.expression
                        } as Measure
                    ])) :
                {}
        };
        this.virtualDevices.push(this.mkVirtualDeviceConfig(vdevId, true));
        this.virtualDevices.sort(keySorter('vdevId'));
        this.vDevToExpand = vdevId; // expand panel of newly added device
        this.reloadPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameVirtualDevice(vdevId: string, newName: string) {
        const vdevCfg = this.virtualDevices.find(vdev => vdev.vdevId === vdevId);
        vdevCfg.vdevId = newName;
        this.cfg.devices[newName] = this.cfg.devices[vdevId];
        delete this.cfg.devices[vdevId];
        this.virtualDevices.sort(keySorter('vdevId'));
        this.reloadPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteVirtualDevice(vdevId: string) {
        const idx = this.virtualDevices.findIndex(vdev => vdev.vdevId === vdevId);
        this.virtualDevices[idx].valueSubscription.unsubscribe();
        this.virtualDevices[idx].statusSubscription.unsubscribe();
        const measures = _.values(this.virtualDevices[idx].measures);
        measures.forEach(meas => {
            meas.valueSubscription.unsubscribe();
            meas.statusSubscription.unsubscribe();
        });
        this.virtualDevices.splice(idx, 1);
        delete this.cfg.devices[vdevId];
        this.reloadPages.next();
        this.updateConfig();
        // validity may change if there are some related measures
        if (measures.length) {
            this.updateValidity();
        }
    }

    private updateSourceConfig(value: { [p: string]: any }) {
        this.cfg.src_cfg.type = value['srcType'];
        switch (this.cfg.src_cfg.type) {
            case SourceType.METEOMATICS:
                (this.cfg.src_cfg as MeteomaticsConfig).api_url = value['apiUrl'];
                (this.cfg.src_cfg as MeteomaticsConfig).username = value['user'];
                (this.cfg.src_cfg as MeteomaticsConfig).password = this.form.controls.pass?.dirty ? value['pass'] : null;
                break;
        }
    }

    private setVirtualDevicePolicy(vdevId: string, policy: PollingPolicy) {
        this.cfg.devices[vdevId] = setDevicePolicy(this.cfg.devices[vdevId], policy);
        this.updateConfig();
        // `policy` is assumed to contain valid data, so no need for validity update
    }

    private setVirtualDeviceAdvancedSettings(vdev: VirtualDeviceConfig,
                                             offset: number,
                                             granularity: number) {
        this.cfg.devices[vdev.vdevId].offset = offset;
        this.cfg.devices[vdev.vdevId].granularity = granularity;
        this.updateConfig();
        // validity has not changed, as advanced settings form has already been validated
    }

    private addMeasure(vdevCfg: VirtualDeviceConfig, measId: string) {
        // config must be updated in order for mkMeasureConfig() to work
        if (!this.cfg.devices[vdevCfg.vdevId].measures) {
            this.cfg.devices[vdevCfg.vdevId].measures = {};
        }
        this.cfg.devices[vdevCfg.vdevId].measures[measId] = {
            type: null,
            uom: null
        };
        vdevCfg.measures[measId] = this.mkMeasureConfig(vdevCfg, measId, true);
        this.updateConfig();
        this.updateValidity();
    }

    private renameMeasure(vdevCfg: VirtualDeviceConfig, measId: string, newName: string) {
        vdevCfg.measures[measId].measId = newName;
        vdevCfg.measures[newName] = vdevCfg.measures[measId];
        delete vdevCfg.measures[measId];
        this.cfg.devices[vdevCfg.vdevId].measures[newName] = this.cfg.devices[vdevCfg.vdevId].measures[measId];
        delete this.cfg.devices[vdevCfg.vdevId].measures[measId];
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteMeasure(vdevCfg: VirtualDeviceConfig, measId: string) {
        vdevCfg.measures[measId].valueSubscription.unsubscribe();
        vdevCfg.measures[measId].statusSubscription.unsubscribe();
        delete vdevCfg.measures[measId];
        delete this.cfg.devices[vdevCfg.vdevId].measures[measId];
        this.updateConfig();
        // validity may change (e.g. in case an invalid measure is deleted)
        this.updateValidity();
    }

    private setMeasureAdvancedSettings(vdev: VirtualDeviceConfig, meas: MeasureConfig,
                                       expression: string) {
        this.cfg.devices[vdev.vdevId].measures[meas.measId].expression = expression && expression.trim().length ? expression.trim() : null;
        this.updateConfig();
        // validity has not changed, as advanced settings form has already been validated
    }

    private mkVirtualDeviceConfig(vdevId: string, newlyCreated: boolean): VirtualDeviceConfig {
        const cfg = {
            vdevId: vdevId,
            form: this.fb.group({
                latitude: new FormControl(this.cfg.devices[vdevId].latitude, [Validators.required, validateFloat, Validators.min(-90), Validators.max(90)]),
                longitude: new FormControl(this.cfg.devices[vdevId].longitude, [Validators.required, validateFloat, Validators.min(-180), Validators.max(180)]),
                duration: new FormControl(this.cfg.devices[vdevId].duration, [Validators.required, validateInteger, validateGt(0)])
            }),
            newlyCreated
        } as VirtualDeviceConfig;
        cfg.measures = _.mapValues(this.cfg.devices[vdevId].measures || {}, (m, mid) => this.mkMeasureConfig(cfg, mid, newlyCreated));
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const vdev = this.cfg.devices[cfg.vdevId];
            vdev.latitude = value['latitude'];
            vdev.longitude = value['longitude'];
            vdev.duration = value['duration'];
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkMeasureConfig(vdevCfg: VirtualDeviceConfig, measId: string, newlyCreated: boolean): MeasureConfig {
        const cfg = {
            measId: measId,
            form: this.fb.group({
                type: new FormControl(this.cfg.devices[vdevCfg.vdevId].measures[measId].type, [Validators.required]),
                uom: new FormControl({
                    value: this.cfg.devices[vdevCfg.vdevId].measures[measId].uom,
                    disabled: !uomEnabled(this.cfg.devices[vdevCfg.vdevId].measures[measId])
                }, [])
            }),
            newlyCreated
        } as MeasureConfig;
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const meas = this.cfg.devices[vdevCfg.vdevId].measures[cfg.measId];
            meas.type = value['type'];
            meas.uom = value['uom'] && value['uom'].trim().length ? value['uom'].trim() : null;
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkMeasureUniquenessValidator(ignoreMeasure?: string): AsyncValidatorFn {
        return ctrl => {
            const measure = !isBlank(ctrl.value) ? ctrl.value.trim() : null;
            if (!measure || (!!ignoreMeasure && measure === ignoreMeasure)) {
                return of(null);
            }
            return this.isMeasureDuplicate(measure).pipe(
                map(isDuplicate => isDuplicate ? {'duplicate': true} : null)
            );
        };
    }

    private isMeasureDuplicate(measure: string): Observable<boolean> {
        // check for duplicates inside this very connector, using *current* values
        if (_.flatten(this.virtualDevices.map(vdev => _.values(vdev.measures))).find(measCfg => measCfg.measId === measure)) {
            return of(true);
        }
        return this.apiService.checkConnectorDeviceExistence(measure).pipe(
            switchMap(resp => resp.value ?
                this.apiService.findConnectorByDevice(measure).pipe(
                    map(idResp => idResp?.id !== this.id) // ignore "this very connector" (already checked against current values)
                ) :
                of(false))
        );
    }

    private mkPagedVirtualDevices(size: number, page: number): Observable<PagedVirtualDevices> {
        let pagedVdevs = [];
        for (let i = 0; i < this.virtualDevices.length; i += size) {
            pagedVdevs.push(this.virtualDevices.slice(i, i + size));
        }
        return of({
            data: pagedVdevs[page],
            total: this.virtualDevices.length
        } as PagedVirtualDevices);
    }
}

function uomEnabled(meas: Measure): boolean {
    return meas.type !== MeasureType.WEATHER_STATE;
}

function advancedSettingsEnabled(meas: Measure): boolean {
    return meas.type !== MeasureType.WEATHER_STATE;
}
